<template>
  <v-card height="100%" flat color="bg1" class="release-notes-settings overflow-y-auto">
    <!-- Page Header -->
    <v-list-item two-line :class="$vuetify.theme.isDark ? 'bg2' : 'white'">
      <v-list-item-content>
        <v-list-item-title class="title">Release Notes Settings</v-list-item-title>
        <v-list-item-subtitle>
          These defaults apply to every release — which Jira project you start from, who receives
          the release email, and when we remind you to deploy.
        </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action v-if="!featureDisabled" class="flex-row align-center">
        <v-btn text class="mr-2" @click="goBack">
          <v-icon left small>mdi-arrow-left</v-icon>
          Back
        </v-btn>
        <v-btn
          v-if="canEdit"
          color="primary"
          :loading="isSaving"
          :disabled="!canSave"
          @click="save">
          <v-icon left small>mdi-content-save-outline</v-icon>
          Save changes
        </v-btn>
      </v-list-item-action>
    </v-list-item>
    <v-divider></v-divider>

    <!-- Feature flag empty state -->
    <release-notes-disabled-notice v-if="featureDisabled" />

    <!-- Loading -->
    <v-sheet v-else-if="isLoading" color="transparent" class="ma-5">
      <v-skeleton-loader type="article, list-item-two-line, article, actions" />
    </v-sheet>

    <v-sheet v-else color="transparent" class="ma-5 settings-body">
      <!-- Read-only notice -->
      <div v-if="!canEdit" class="ds-info-box ds-info-box--info mb-6">
        <v-icon class="ds-info-box__icon" small>mdi-lock-outline</v-icon>
        <div class="ds-info-box__content">
          You can view these settings, but changing them needs the Release Notes Settings
          permission. Ask a management administrator if you need it.
        </div>
      </div>

      <v-form ref="form" v-model="valid">
        <!-- ------------------------------------------------ Jira -->
        <div class="ds-section-header">
          <div class="ds-section-header__content">
            <h3 class="ds-section-header__title">Jira</h3>
            <p class="ds-section-header__subtitle">
              Where releases are pulled from when you start a new one.
            </p>
          </div>
        </div>

        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.defaultJiraProjectKey"
              :items="jiraProjectItems"
              :loading="isProjectsLoading"
              label="Default Jira project"
              outlined
              dense
              clearable
              :disabled="!canEdit"
              hint="Pre-selected in the version picker when you start a new release."
              persistent-hint />
          </v-col>
        </v-row>

        <!-- ------------------------------------------------ Audience -->
        <div class="ds-section-header mt-8">
          <div class="ds-section-header__content">
            <h3 class="ds-section-header__title">Audience</h3>
            <p class="ds-section-header__subtitle">
              Who receives a release email by default when you publish.
            </p>
          </div>
        </div>

        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.defaultRecipientPolicy"
              :items="recipientPolicyItems"
              label="Default recipient policy"
              outlined
              dense
              :disabled="!canEdit"
              :rules="[formRules.requiredNumber]" />
          </v-col>
        </v-row>

        <!-- ------------------------------------------------ Reminder -->
        <div class="ds-section-header mt-4">
          <div class="ds-section-header__content">
            <h3 class="ds-section-header__title">Deployment reminder</h3>
            <p class="ds-section-header__subtitle">
              After publishing, we can remind whoever ships the app that the notes are live.
            </p>
          </div>
        </div>

        <v-switch
          v-model="form.reminderEnabledByDefault"
          class="mt-0"
          inset
          dense
          :disabled="!canEdit"
          label="Schedule a reminder by default when publishing" />

        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="form.reminderDelayDays"
              label="Reminder delay (days)"
              type="number"
              min="1"
              max="90"
              outlined
              dense
              :disabled="!canEdit || !form.reminderEnabledByDefault"
              :rules="delayDaysRules"
              hint="How long after publishing the reminder is due. Between 1 and 90 days."
              persistent-hint />
          </v-col>
        </v-row>

        <p class="ds-field-label mt-6 mb-1">Reminder recipients</p>
        <v-radio-group
          v-model="form.reminderRecipientMode"
          class="mt-0"
          dense
          :disabled="!canEdit || !form.reminderEnabledByDefault">
          <v-radio label="Configured email addresses" :value="reminderModes.Emails" />
          <v-radio label="Holders of the publish permission" :value="reminderModes.Permission" />
        </v-radio-group>

        <template v-if="form.reminderRecipientMode === reminderModes.Emails">
          <v-combobox
            v-model="form.reminderRecipientEmails"
            label="Reminder email addresses"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            :disabled="!canEdit || !form.reminderEnabledByDefault"
            :rules="reminderEmailsRules"
            hint="Type an address and press Enter to add it."
            persistent-hint />

          <div v-if="showNoRecipientsWarning" class="ds-info-box ds-info-box--warning mt-4">
            <v-icon class="ds-info-box__icon" small>mdi-alert-outline</v-icon>
            <div class="ds-info-box__content">
              {{ noRecipientsMessage }}
            </div>
          </div>
        </template>

        <!-- ------------------------------------------------ Icons -->
        <div class="ds-section-header mt-8">
          <div class="ds-section-header__content">
            <h3 class="ds-section-header__title">Heading icons</h3>
            <p class="ds-section-header__subtitle">
              Emoji in front of the release title and each section heading, so notes are easy to
              scan. Applied when notes are generated — existing drafts keep whatever they were
              written with.
            </p>
          </div>
        </div>

        <v-switch
          v-model="form.includeSectionIcons"
          class="mt-0"
          inset
          dense
          :disabled="!canEdit"
          label="Show icons in release notes headings" />

        <template v-if="form.includeSectionIcons">
          <div class="release-notes-settings__icon-preview mb-4">
            <span v-for="row in iconRows" :key="row.key" class="release-notes-settings__icon-chip">
              <span class="release-notes-settings__icon-glyph">{{ form.sectionIcons[row.key] }}</span>
              {{ row.label }}
            </span>
          </div>

          <v-expansion-panels flat class="release-notes-settings__icon-panel">
            <v-expansion-panel>
              <v-expansion-panel-header class="px-0">Customise icons</v-expansion-panel-header>
              <v-expansion-panel-content class="px-0">
                <v-row dense>
                  <v-col v-for="row in iconRows" :key="row.key" cols="6" sm="4" md="3">
                    <v-text-field
                      v-model="form.sectionIcons[row.key]"
                      :label="row.label"
                      outlined
                      dense
                      :disabled="!canEdit"
                      :rules="iconRules"
                      class="release-notes-settings__icon-field" />
                  </v-col>
                </v-row>
                <v-btn
                  v-if="canEdit"
                  x-small
                  text
                  :disabled="areIconsDefault"
                  @click="resetIcons">
                  <v-icon left x-small>mdi-backup-restore</v-icon>
                  Reset icons to default
                </v-btn>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <!-- ------------------------------------------------ AI model -->
        <div class="ds-section-header mt-8">
          <div class="ds-section-header__content">
            <h3 class="ds-section-header__title">AI model</h3>
            <p class="ds-section-header__subtitle">
              Which OpenAI model writes the release notes, and how much it reasons first. Applies to
              the next generation — existing drafts are untouched.
            </p>
          </div>
        </div>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.aiModel"
              label="Model"
              outlined
              dense
              :placeholder="defaultAiModel || undefined"
              :disabled="!canEdit"
              :rules="aiModelRules"
              :hint="aiModelHint"
              persistent-hint />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.reasoningEffort"
              :items="reasoningEffortItems"
              label="Reasoning effort"
              outlined
              dense
              :disabled="!canEdit"
              hint="Higher effort is slower and uses more tokens, but handles long ticket lists better."
              persistent-hint />
          </v-col>
        </v-row>

        <div class="ds-info-box ds-info-box--info mt-4">
          <v-icon class="ds-info-box__icon" small>mdi-information-outline</v-icon>
          <div class="ds-info-box__content">
            Reasoning effort only works with reasoning models. If you pick a model that does not
            support it, set the effort to Default or generation will fail.
          </div>
        </div>

        <!-- ------------------------------------------------ AI prompt -->
        <div class="ds-section-header mt-8">
          <div class="ds-section-header__content">
            <h3 class="ds-section-header__title">AI prompt</h3>
            <p class="ds-section-header__subtitle">
              The instructions the AI follows when it writes release notes. Edits apply to the next
              generation — existing drafts are untouched.
            </p>
          </div>
        </div>

        <div class="ds-info-box ds-info-box--info mb-4">
          <v-icon class="ds-info-box__icon" small>mdi-information-outline</v-icon>
          <div class="ds-info-box__content">
            Whatever you write here, generated notes are still validated against the ticket list: the
            AI cannot invent a Jira key, and ticket text is always treated as data rather than as
            instructions.
          </div>
        </div>

        <div class="d-flex align-center mb-1">
          <label for="release-system-prompt" class="ds-field-label mb-0">System prompt</label>
          <v-spacer />
          <v-btn
            v-if="canEdit"
            x-small
            text
            :disabled="isSystemPromptDefault"
            @click="resetSystemPrompt">
            <v-icon left x-small>mdi-backup-restore</v-icon>
            Reset to default
          </v-btn>
        </div>
        <v-textarea
          id="release-system-prompt"
          v-model="form.systemPrompt"
          class="release-notes-settings__prompt"
          outlined
          dense
          auto-grow
          rows="14"
          :disabled="!canEdit"
          :rules="promptRules"
          hint="Who the AI is writing for and the rules it must follow."
          persistent-hint />

        <div class="d-flex align-center mb-1 mt-6">
          <label for="release-style-guide" class="ds-field-label mb-0">Style guide</label>
          <v-spacer />
          <v-btn
            v-if="canEdit"
            x-small
            text
            :disabled="isStyleGuideDefault"
            @click="resetStyleGuide">
            <v-icon left x-small>mdi-backup-restore</v-icon>
            Reset to default
          </v-btn>
        </div>
        <v-textarea
          id="release-style-guide"
          v-model="form.styleGuide"
          class="release-notes-settings__prompt"
          outlined
          dense
          auto-grow
          rows="10"
          :disabled="!canEdit"
          :rules="promptRules"
          hint="Tone and wording rules, appended to the system prompt."
          persistent-hint />
      </v-form>
    </v-sheet>
  </v-card>
</template>

<script>
  import { $has } from '@Utils';
  import { error } from '@/utils/debugLogger';
  import formRules from '@Share/config/form-rules';
  import permissions from '@Share/config/permissions';
  import {
    RELEASE_ERROR_MESSAGES,
    ReleaseRecipientPolicy,
    ReleaseRecipientPolicyLabels,
    ReleaseReminderRecipientMode,
  } from '@ReleaseNotes/config/enums';
  import ReleaseNotesDisabledNotice from '@ReleaseNotes/components/ReleaseNotesDisabledNotice.vue';

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const REMINDER_MODES = Object.freeze({
    Emails: 'emails',
    Permission: 'permission',
  });

  /**
   * Select value for "no override — follow the server default". Sent to the API as null; a real
   * string keeps v-select from treating the choice as an empty selection.
   */
  const DEFAULT_REASONING_EFFORT = 'default';

  /** Fallback when the API does not send reasoningEffortOptions. Matches ReleaseNotesReasoningEffort.Allowed. */
  const REASONING_EFFORTS = Object.freeze(['minimal', 'low', 'medium', 'high']);

  /** Matches ReleaseNotesSettings.MaxAiModelLength and the API's model-name check. */
  const MAX_AI_MODEL_LENGTH = 100;
  const AI_MODEL_PATTERN = /^[A-Za-z0-9._:-]*$/;

  const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

  /** Shape used for a form that has nothing to seed from (fetch failed / feature off). */
  function emptyForm() {
    return {
      defaultRecipientPolicy: ReleaseRecipientPolicy.CompanyOwners,
      reminderEnabledByDefault: false,
      reminderDelayDays: 7,
      reminderRecipientMode: REMINDER_MODES.Permission,
      reminderRecipientEmails: [],
      defaultJiraProjectKey: null,
      systemPrompt: '',
      styleGuide: '',
      includeSectionIcons: true,
      sectionIcons: emptyIcons(),
      aiModel: '',
      reasoningEffort: DEFAULT_REASONING_EFFORT,
    };
  }

  /** Mirrors the API's ReleaseNotesIcons; the server fills in the recommended emoji. */
  function emptyIcons() {
    return {
      releaseTitle: '',
      highlights: '',
      features: '',
      improvements: '',
      fixes: '',
      security: '',
      breaking: '',
      knownIssues: '',
      actionRequired: '',
    };
  }

  /** Heading order as it appears in the notes, so the editor reads top to bottom like the output. */
  const ICON_ROWS = Object.freeze([
    { key: 'releaseTitle', label: 'Release title' },
    { key: 'highlights', label: 'Highlights' },
    { key: 'features', label: 'New Features' },
    { key: 'improvements', label: 'Improvements' },
    { key: 'fixes', label: 'Bug Fixes' },
    { key: 'security', label: 'Security' },
    { key: 'breaking', label: 'Breaking Changes' },
    { key: 'knownIssues', label: 'Known Issues' },
    { key: 'actionRequired', label: 'Action Required' },
  ]);

  /** Matches ReleaseNotesIcons.MaxIconLength on the API. */
  const MAX_ICON_LENGTH = 16;

  /** Matches ReleaseNotesSettings.MaxPromptLength on the API. */
  const MAX_PROMPT_LENGTH = 20000;

  export default {
    name: 'ReleaseNotesSettings',
    components: { ReleaseNotesDisabledNotice },
    data() {
      return {
        permissions,
        formRules,
        reminderModes: REMINDER_MODES,
        valid: true,
        featureDisabled: false,
        iconRows: ICON_ROWS,
        // The packaged prompt text and icon set as shipped — what "Reset to default" restores.
        defaultSystemPrompt: '',
        defaultStyleGuide: '',
        defaultSectionIcons: emptyIcons(),
        // What the server resolves to when no model/effort override is stored (appsettings).
        defaultAiModel: '',
        defaultReasoningEffort: null,
        reasoningEffortOptions: REASONING_EFFORTS,
        form: emptyForm(),
        // JSON snapshot of the last persisted form state — drives dirty tracking.
        baseline: JSON.stringify(emptyForm()),
      };
    },
    computed: {
      settings() {
        return this.$store.getters['RELEASE_NOTES/GET_SETTINGS'];
      },
      isLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_SETTINGS_LOADING'];
      },
      isSaving() {
        return this.$store.getters['RELEASE_NOTES/IS_SETTINGS_SAVING'];
      },
      jiraProjects() {
        return this.$store.getters['RELEASE_NOTES/GET_JIRA_PROJECTS'];
      },
      isProjectsLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_JIRA_PROJECTS_LOADING'];
      },
      canEdit() {
        return this.$has(permissions.ReleaseNotes.Settings);
      },
      recipientPolicyItems() {
        return Object.keys(ReleaseRecipientPolicyLabels).map((key) => ({
          value: Number(key),
          text: ReleaseRecipientPolicyLabels[key],
        }));
      },
      /**
       * Only the projects the server is configured for: a default outside that allowlist would be
       * refused on save (RELEASE_JIRA_PROJECT_NOT_ALLOWED), so it is never offered here.
       */
      jiraProjectItems() {
        const projects = Array.isArray(this.jiraProjects) ? this.jiraProjects : [];
        return projects.map((project) => ({
          value: project.key,
          text: project.name && project.name !== project.key ? `${project.key} — ${project.name}` : project.key,
        }));
      },
      isSystemPromptDefault() {
        return (this.form.systemPrompt || '').trim() === (this.defaultSystemPrompt || '').trim();
      },
      isStyleGuideDefault() {
        return (this.form.styleGuide || '').trim() === (this.defaultStyleGuide || '').trim();
      },
      promptRules() {
        return [
          (v) =>
            (v || '').length <= MAX_PROMPT_LENGTH ||
            `Must be at most ${MAX_PROMPT_LENGTH} characters.`,
        ];
      },
      aiModelRules() {
        // Empty is allowed and means "use the default model".
        return [
          (v) =>
            (v || '').trim().length <= MAX_AI_MODEL_LENGTH ||
            `Must be at most ${MAX_AI_MODEL_LENGTH} characters.`,
          (v) =>
            AI_MODEL_PATTERN.test((v || '').trim()) ||
            'Use letters, digits, dots, dashes, underscores or colons only.',
        ];
      },
      aiModelHint() {
        return this.defaultAiModel
          ? `Leave empty to use the default (${this.defaultAiModel}).`
          : 'Leave empty to use the default model.';
      },
      reasoningEffortItems() {
        const defaultLabel = this.defaultReasoningEffort
          ? `Default (${capitalize(this.defaultReasoningEffort)})`
          : 'Default (model decides)';
        return [
          { value: DEFAULT_REASONING_EFFORT, text: defaultLabel },
          ...this.reasoningEffortOptions.map((effort) => ({ value: effort, text: capitalize(effort) })),
        ];
      },
      iconRules() {
        // Empty is allowed and means "no icon on this heading".
        return [
          (v) => (v || '').length <= MAX_ICON_LENGTH || `Must be at most ${MAX_ICON_LENGTH} characters.`,
        ];
      },
      areIconsDefault() {
        return ICON_ROWS.every(
          (row) => (this.form.sectionIcons[row.key] || '') === (this.defaultSectionIcons[row.key] || ''),
        );
      },
      isDirty() {
        return JSON.stringify(this.form) !== this.baseline;
      },
      canSave() {
        return this.canEdit && this.valid && this.isDirty && !this.isSaving;
      },
      delayDaysRules() {
        return [
          (v) => {
            // Only enforced while reminders are on by default; the field is disabled otherwise.
            if (!this.form.reminderEnabledByDefault) return true;
            if (v === null || v === undefined || String(v).trim() === '') {
              return 'This field is required!';
            }
            if (!/^\d+$/.test(String(v).trim())) {
              return 'Enter a whole number of days.';
            }
            const days = Number(v);
            if (days < 1 || days > 90) return 'Must be between 1 and 90 days.';
            return true;
          },
        ];
      },
      reminderEmailsRules() {
        return [
          (v) => {
            const invalid = (v || [])
              .map((entry) => String(entry).trim())
              .filter((entry) => entry.length > 0 && !EMAIL_PATTERN.test(entry));
            if (invalid.length === 0) return true;
            return `Not a valid email address: ${invalid.join(', ')}`;
          },
        ];
      },
      showNoRecipientsWarning() {
        return (
          this.form.reminderEnabledByDefault &&
          this.form.reminderRecipientMode === REMINDER_MODES.Emails &&
          (this.form.reminderRecipientEmails || []).length === 0
        );
      },
      noRecipientsMessage() {
        return RELEASE_ERROR_MESSAGES.RELEASE_REMINDER_NO_RECIPIENTS;
      },
    },
    created() {
      this.load();
    },
    methods: {
      $has,

      /**
       * Initial load. FETCH_SETTINGS swallows its error (it toasts via $handleError and resolves
       * with `undefined`), so the feature-disabled case is inferred from "no settings came back"
       * rather than from `isFeatureDisabledError`.
       */
      async load() {
        const result = await this.$store.dispatch('RELEASE_NOTES/FETCH_SETTINGS');
        const settings = result || this.settings;
        if (!settings) {
          this.featureDisabled = true;
          return;
        }
        this.seed(settings);
        this.loadJiraProjects();
      },

      /**
       * Populates the default-project picker. The action commits the list to the store and owns
       * its own loading flag, so nothing is assigned here — it re-throws, and a Jira outage must
       * leave the field empty rather than break the page.
       */
      async loadJiraProjects() {
        try {
          await this.$store.dispatch('RELEASE_NOTES/FETCH_JIRA_PROJECTS');
        } catch (err) {
          // FETCH_JIRA_PROJECTS already surfaced the error via $handleError.
          error('Release notes settings: could not load Jira projects', err);
        }
      },

      /** Applies a settings DTO to the form and resets the dirty baseline. */
      seed(settings) {
        const s = settings || {};
        // Kept outside the form so they never count as dirty; they only back "Reset to default".
        this.defaultSystemPrompt = s.defaultSystemPrompt ?? '';
        this.defaultStyleGuide = s.defaultStyleGuide ?? '';
        this.defaultSectionIcons = { ...emptyIcons(), ...(s.defaultSectionIcons || {}) };
        this.defaultAiModel = s.defaultAiModel ?? '';
        this.defaultReasoningEffort = s.defaultReasoningEffort ?? null;
        this.reasoningEffortOptions =
          Array.isArray(s.reasoningEffortOptions) && s.reasoningEffortOptions.length > 0
            ? s.reasoningEffortOptions
            : REASONING_EFFORTS;
        this.form = this.mapSettingsToForm(settings);
        this.baseline = JSON.stringify(this.form);
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.resetValidation();
        });
      },

      /**
       * Maps the settings DTO onto the local form. The DTO's exact property names are not pinned
       * by the API contract yet, so each field probes the plausible spellings before defaulting.
       * @param {object} settings raw response from GET Settings
       */
      mapSettingsToForm(settings) {
        const s = settings || {};
        // The API sends the numeric ReleaseReminderRecipientMode enum; the string branch is kept
        // only so a hand-written fixture ("PublishPermissionHolders") still reads correctly.
        const rawMode = s.reminderRecipientMode ?? null;

        let mode;
        if (typeof rawMode === 'number') {
          mode =
            rawMode === ReleaseReminderRecipientMode.PublishPermissionHolders
              ? REMINDER_MODES.Permission
              : REMINDER_MODES.Emails;
        } else if (typeof rawMode === 'string' && rawMode.trim().length > 0) {
          mode = /perm/i.test(rawMode) ? REMINDER_MODES.Permission : REMINDER_MODES.Emails;
        } else {
          mode = REMINDER_MODES.Emails;
        }

        // The API stores the recipient list as one comma-separated string; the chip input wants an array.
        const rawEmails = s.reminderRecipientEmails ?? s.reminderEmails ?? [];
        const emails =
          typeof rawEmails === 'string'
            ? rawEmails
                .split(',')
                .map((entry) => entry.trim())
                .filter((entry) => entry.length > 0)
            : rawEmails;

        return {
          defaultRecipientPolicy:
            s.defaultRecipientPolicy ?? s.recipientPolicy ?? ReleaseRecipientPolicy.CompanyOwners,
          reminderEnabledByDefault: !!(s.reminderEnabledByDefault ?? s.reminderEnabled ?? false),
          reminderDelayDays: String(s.reminderDelayDays ?? s.defaultReminderDelayDays ?? 7),
          reminderRecipientMode: mode,
          reminderRecipientEmails: Array.isArray(emails) ? emails.slice() : [],
          defaultJiraProjectKey: s.defaultJiraProjectKey ?? null,
          // Effective values: the stored override when there is one, the packaged text otherwise.
          systemPrompt: s.systemPrompt ?? '',
          styleGuide: s.styleGuide ?? '',
          includeSectionIcons: s.includeSectionIcons !== false,
          sectionIcons: { ...emptyIcons(), ...(s.sectionIcons || {}) },
          // Stored overrides only (null = follow the default), unlike the prompts above.
          aiModel: s.aiModel ?? '',
          reasoningEffort: s.reasoningEffort || DEFAULT_REASONING_EFFORT,
        };
      },

      /** Reverse of `mapSettingsToForm` — the body sent to PUT Settings. */
      mapFormToPayload() {
        const usePermissionHolders = this.form.reminderRecipientMode === REMINDER_MODES.Permission;
        return {
          defaultRecipientPolicy: Number(this.form.defaultRecipientPolicy),
          reminderEnabledByDefault: !!this.form.reminderEnabledByDefault,
          reminderDelayDays: Number(this.form.reminderDelayDays),
          reminderRecipientMode: usePermissionHolders
            ? ReleaseReminderRecipientMode.PublishPermissionHolders
            : ReleaseReminderRecipientMode.ConfiguredEmails,
          reminderRecipientEmails: usePermissionHolders
            ? ''
            : (this.form.reminderRecipientEmails || [])
                .map((entry) => String(entry).trim())
                .filter((entry) => entry.length > 0)
                .join(','),
          defaultJiraProjectKey: this.form.defaultJiraProjectKey || null,
          // Sending text identical to the packaged default clears the override server-side, so the
          // platform keeps following the shipped prompt if a later release revises it.
          systemPrompt: this.form.systemPrompt || '',
          styleGuide: this.form.styleGuide || '',
          includeSectionIcons: !!this.form.includeSectionIcons,
          // Sending the recommended set clears the override server-side, same as for the prompts.
          sectionIcons: { ...this.form.sectionIcons },
          // Null (or the default itself) clears the override server-side.
          aiModel: (this.form.aiModel || '').trim() || null,
          reasoningEffort:
            this.form.reasoningEffort === DEFAULT_REASONING_EFFORT ? null : this.form.reasoningEffort,
        };
      },

      resetIcons() {
        this.form.sectionIcons = { ...this.defaultSectionIcons };
      },

      resetSystemPrompt() {
        this.form.systemPrompt = this.defaultSystemPrompt;
      },
      resetStyleGuide() {
        this.form.styleGuide = this.defaultStyleGuide;
      },

      async save() {
        if (!this.canEdit) return;
        if (this.$refs.form && !this.$refs.form.validate()) return;

        try {
          const updated = await this.$store.dispatch(
            'RELEASE_NOTES/UPDATE_SETTINGS',
            this.mapFormToPayload(),
          );
          // Re-seed from the server response so the dirty flag clears against what was persisted.
          this.seed(updated || this.settings);
          this.$dialog.notify.success('Settings saved', {
            position: 'top-right',
            timeout: 3000,
          });
        } catch (err) {
          // UPDATE_SETTINGS already surfaced the error via $handleError.
          error('Failed to save release notes settings', err);
        }
      },

      async goBack() {
        if (this.isDirty && this.canEdit) {
          const confirmed = await this.$dialog.confirm({
            title: 'Discard changes?',
            text: 'You have unsaved changes to these settings. Leaving now discards them.',
            actions: {
              false: 'Stay',
              true: { text: 'Discard', color: 'error' },
            },
          });
          if (!confirmed) return;
        }
        this.$router.push('/manage-release-notes');
      },
    },
  };
</script>

<style scoped lang="scss">
  .settings-body {
    max-width: 900px;
  }

  .release-notes-settings {
    ::v-deep .v-input--switch .v-messages__message {
      color: var(--v-text_secondary-base);
    }

    &__icon-preview {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    &__icon-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      border-radius: 14px;
      background-color: var(--v-bg2-base);
      font-size: 12px;
      color: var(--v-text_secondary-base);
    }

    &__icon-glyph {
      font-size: 15px;
      line-height: 1;
    }

    &__icon-panel ::v-deep .v-expansion-panel-header {
      min-height: 36px;
      font-size: 13px;
    }

    &__icon-field ::v-deep input {
      font-size: 16px;
    }

    // The prompts are Markdown the user edits as source, so they read as source.
    &__prompt ::v-deep textarea {
      font-family: 'Roboto Mono', 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
      font-size: 12.5px;
      line-height: 1.55;
    }
  }
</style>
